import { makeAutoObservable, action } from 'mobx';
import {
  fetchRecentlyContactedCandidates,
  removeCandidateFromRecentlyContacted,
  updateHiredStatus
} from '../api/SlmService';
import notificationStore from './NotificationStore';

class RecentlyContactedStore {
  recentlyContacted = [];

  isRecentlyContactedLoading = false;

  recentlyContactedPaginationInfo = {};

  page = 1;

  sort = {
    by: null,
    order: null
  };

  safeItemRemove = false;

  _navigate = null;

  _query = null;

  notificationStore = {};

  /* Used in case when remove last item on page and page value should be decremented by 1 before data fetch */
  setSafeItemRemove = (value, query = null, navigate = null) => {
    this.safeItemRemove = value;
    this._query = query;
    this._navigate = navigate;
  };

  constructor(newNotificationStore) {
    this.notificationStore = newNotificationStore;
    makeAutoObservable(this);
  }

  resetSort = () => {
    this.sort = {
      by: null,
      order: null
    };
  };

  setSort = (next) => {
    this.sort = { ...next };
  };

  setPage = (p) => {
    this.page = p;
  };

  setRecentlyContactedPaginationInfo = (paginationInfo) => {
    this.recentlyContactedPaginationInfo = paginationInfo;
  };

  setIsRecentlyContactedLoading = (bool) => {
    this.isRecentlyContactedLoading = bool;
  };

  setRecentlyContacted = (list) => {
    this.recentlyContacted = list;
  };

  fetchRecentlyContacted = (withLoader = true) => {
    if (withLoader) {
      this.setIsRecentlyContactedLoading(true);
    }
    fetchRecentlyContactedCandidates(this.page, this.sort)
      .then(
        action('fetchSuccess', (res) => {
          this.setRecentlyContacted(res.hiredCandidates);
          this.setRecentlyContactedPaginationInfo(res.metadata.paginationInfo);
        }),
        action('fetchError', () => {
          this.notificationStore.setError('Failed to retrieve recently contacted list.');
        })
      ).finally(() => {
        this.setIsRecentlyContactedLoading(false);
      });
  };

  /**
   * First shallow update on the ui state then another fetch too get the updated list
   * @param candidate
   */
  removeFromRecentlyContacted = (candidateId) => {
    const oldList = [...this.recentlyContacted];
    const tempList = [...this.recentlyContacted];
    const idx = tempList.findIndex((item) => item.candidateId === candidateId);
    tempList.splice(idx, 1);
    this.setRecentlyContacted(tempList);

    const isSafeRemoveCriteria = [this.safeItemRemove, this._query, this._history, !tempList.length, this.page > 1];
    if (isSafeRemoveCriteria.every((safeRemoveCriteria) => safeRemoveCriteria === true)) {
      this._query.set('page', this.page - 1);
      this._navigate({ search: this._query.toString() });
    }

    removeCandidateFromRecentlyContacted(candidateId).then(
      action('fetchSuccess', () => {
        this.fetchRecentlyContacted(false);
      }),
      action('fetchError', () => {
        this.setRecentlyContacted(oldList);
        this.notificationStore.setError('Failed to remove candidate from recently contacted list.');
      })
    );
  };

  /**
   * First shallow update on the ui state then another fetch too get the updated list
   * @param candidate
   */
  updateRecentlyContactedHiredStatus = (candidate) => {
    const tempList = [...this.recentlyContacted];
    const oldList = [...this.recentlyContacted];
    const idx = tempList.findIndex((item) => item.candidateId === candidate.candidateId);
    const updatedCandidate = {
      ...candidate,
      candidateHired: !candidate.candidateHired
    };
    tempList.splice(idx, 1, updatedCandidate);
    this.setRecentlyContacted(tempList);

    updateHiredStatus(candidate.candidateId, !candidate.candidateHired).then(
      action('fetchSuccess', () => {
        this.fetchRecentlyContacted(false);
      }),
      action('fetchError', () => {
        this.setRecentlyContacted(oldList);
        this.notificationStore.setError('Failed to toggle candidate hired status.');
      })
    );
  };
}

export default new RecentlyContactedStore(notificationStore);
